package com.bulut.mecitogullari.ui.screens.addtransaction

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.ArrowBack
import androidx.compose.material.icons.filled.Delete
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Text
import androidx.compose.material3.TopAppBar
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.unit.dp
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.Switch
import com.bulut.mecitogullari.core.i18n.LocalStrings
import com.bulut.mecitogullari.core.i18n.unitDisplayLabel
import com.bulut.mecitogullari.core.i18n.fillTemplate
import com.bulut.mecitogullari.data.model.TransactionType
import com.bulut.mecitogullari.ui.components.AmountField
import com.bulut.mecitogullari.ui.components.DateField
import com.bulut.mecitogullari.ui.components.DropdownField
import com.bulut.mecitogullari.ui.theme.AppColors
import org.koin.compose.viewmodel.koinViewModel
import org.koin.core.parameter.parametersOf

private const val NO_PROJECT_OPTION = "Genel (projesiz)"

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun AddTransactionScreen(
    transactionId: String,
    onClose: () -> Unit,
    viewModel: AddTransactionViewModel = koinViewModel { parametersOf(transactionId) },
) {
    val uiState by viewModel.uiState.collectAsStateWithLifecycle()
    val strings = LocalStrings.current

    LaunchedEffect(uiState.closed) {
        if (uiState.closed) {
            viewModel.onClosedHandled()
            onClose()
        }
    }

    Scaffold(
        containerColor = AppColors.Background,
        topBar = {
            TopAppBar(
                title = { Text(if (uiState.isEditing) strings.editTransaction else strings.newTransaction) },
                navigationIcon = {
                    IconButton(onClick = onClose) {
                        Icon(Icons.Default.ArrowBack, contentDescription = strings.back)
                    }
                },
                actions = {
                    if (uiState.isEditing) {
                        IconButton(onClick = viewModel::delete) {
                            Icon(Icons.Default.Delete, contentDescription = "Sil", tint = AppColors.Expense)
                        }
                    }
                },
            )
        },
    ) { padding ->
        Column(
            modifier = Modifier
                .fillMaxSize()
                .padding(padding)
                .verticalScroll(rememberScrollState())
                .padding(16.dp),
            verticalArrangement = Arrangement.spacedBy(14.dp),
        ) {
            TypeSelector(
                selected = uiState.type,
                onSelect = viewModel::onTypeChange,
            )

            DateField(
                label = strings.dateLabel,
                dateEpochMillis = uiState.dateEpochMillis,
                onDateSelected = viewModel::onDateChange,
            )

            OutlinedTextField(
                value = uiState.description,
                onValueChange = viewModel::onDescriptionChange,
                label = { Text(strings.itemNameLabel) },
                singleLine = true,
                shape = RoundedCornerShape(12.dp),
                modifier = Modifier.fillMaxWidth(),
            )

            AmountField(
                label = strings.amountFieldLabel,
                value = uiState.amountText,
                onValueChange = viewModel::onAmountChange,
                modifier = Modifier.fillMaxWidth(),
            )

            DropdownField(
                label = if (uiState.type == TransactionType.INCOME) strings.incomeType else strings.expenseCategory,
                value = uiState.category,
                options = uiState.categoryOptions,
                onSelect = viewModel::onCategoryChange,
            )

            DropdownField(
                label = strings.project,
                value = uiState.projects.firstOrNull { it.id == uiState.projectId }?.name
                    ?: if (uiState.isApartmentSale) "" else NO_PROJECT_OPTION,
                options = if (uiState.isApartmentSale) {
                    uiState.projects.map { it.name }
                } else {
                    listOf(NO_PROJECT_OPTION) + uiState.projects.map { it.name }
                },
                onSelect = { selected ->
                    viewModel.onProjectChange(
                        uiState.projects.firstOrNull { it.name == selected }?.id
                    )
                },
            )

            if (uiState.isApartmentSale && uiState.projectId != null) {
                val unitLabels = uiState.availableUnits.map {
                    unitDisplayLabel(it.block, it.number) + " (" +
                        strings.floorShort.fillTemplate("floor" to it.floor.toString()) + ")"
                }
                DropdownField(
                    label = strings.soldUnitLabel,
                    value = uiState.availableUnits
                        .firstOrNull { it.id == uiState.unitId }
                        ?.let {
                            unitDisplayLabel(it.block, it.number) + " (" +
                                strings.floorShort.fillTemplate("floor" to it.floor.toString()) + ")"
                        } ?: "",
                    options = unitLabels,
                    onSelect = { selected ->
                        val index = unitLabels.indexOf(selected)
                        viewModel.onUnitChange(uiState.availableUnits.getOrNull(index)?.id)
                    },
                )
                if (uiState.availableUnits.isEmpty()) {
                    Text(
                        text = strings.noUnitsAvailable,
                        style = MaterialTheme.typography.bodySmall,
                        color = AppColors.Expense,
                    )
                } else {
                    Text(
                        text = strings.saleMarksUnit,
                        style = MaterialTheme.typography.bodySmall,
                        color = AppColors.Income,
                    )
                }
            }

            // Kasaya girmeyen (kart/havale) harcamalar sadece projeye yazılabilir.
            Card(
                shape = RoundedCornerShape(14.dp),
                colors = CardDefaults.cardColors(containerColor = Color.White),
                elevation = CardDefaults.cardElevation(defaultElevation = 1.dp),
                modifier = Modifier.fillMaxWidth(),
            ) {
                Column(modifier = Modifier.padding(14.dp)) {
                    Row(verticalAlignment = Alignment.CenterVertically) {
                        Text(
                            text = strings.addToDailyLedger,
                            style = MaterialTheme.typography.bodyLarge,
                            fontWeight = FontWeight.Medium,
                            modifier = Modifier.weight(1f),
                        )
                        Switch(
                            checked = uiState.inDailyLedger,
                            onCheckedChange = viewModel::onDailyLedgerChange,
                        )
                    }
                    Text(
                        text = if (uiState.inDailyLedger) {
                            strings.addToDailyLedgerHintOn
                        } else {
                            strings.addToDailyLedgerHintOff
                        },
                        style = MaterialTheme.typography.bodySmall,
                        color = if (uiState.inDailyLedger) {
                            AppColors.TextSecondary
                        } else {
                            AppColors.Reserved
                        },
                        modifier = Modifier.padding(top = 4.dp),
                    )
                }
            }

            uiState.error?.let { error ->
                Text(
                    text = error,
                    color = AppColors.Expense,
                    style = MaterialTheme.typography.bodySmall,
                )
            }

            Button(
                onClick = viewModel::save,
                enabled = !uiState.isSaving,
                shape = RoundedCornerShape(12.dp),
                colors = ButtonDefaults.buttonColors(containerColor = AppColors.Navy),
                modifier = Modifier.fillMaxWidth().height(52.dp),
            ) {
                Text(strings.save, style = MaterialTheme.typography.titleMedium, color = Color.White)
            }
        }
    }
}

/** Green/red segmented Gelir | Gider selector. */
@Composable
private fun TypeSelector(
    selected: TransactionType,
    onSelect: (TransactionType) -> Unit,
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .background(Color.White, RoundedCornerShape(14.dp))
            .padding(6.dp),
        horizontalArrangement = Arrangement.spacedBy(6.dp),
    ) {
        TypeOption(
            text = "Gelir",
            isSelected = selected == TransactionType.INCOME,
            activeColor = AppColors.Income,
            modifier = Modifier.weight(1f),
            onClick = { onSelect(TransactionType.INCOME) },
        )
        TypeOption(
            text = "Gider",
            isSelected = selected == TransactionType.EXPENSE,
            activeColor = AppColors.Expense,
            modifier = Modifier.weight(1f),
            onClick = { onSelect(TransactionType.EXPENSE) },
        )
    }
}

@Composable
private fun TypeOption(
    text: String,
    isSelected: Boolean,
    activeColor: Color,
    modifier: Modifier = Modifier,
    onClick: () -> Unit,
) {
    Box(
        modifier = modifier
            .background(
                color = if (isSelected) activeColor else Color.Transparent,
                shape = RoundedCornerShape(10.dp),
            )
            .clickable(onClick = onClick)
            .padding(vertical = 12.dp),
        contentAlignment = Alignment.Center,
    ) {
        Text(
            text = text,
            color = if (isSelected) Color.White else AppColors.TextSecondary,
            fontWeight = if (isSelected) FontWeight.SemiBold else FontWeight.Normal,
        )
    }
}
